import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fetchLMSMasterReport, LMSMasterColumn } from '../services/lmsMasterService';
import { exportQueryResponse } from '../services/exportService';

export type LMSMasterReportKey =
  | 'branch-report'
  | 'store-marketing-attributes'
  | 'store-attributes';

export interface LMSMasterContext {
  report_family: 'lms-master';
  active_tab: LMSMasterReportKey;
  report_title: string;
  report_view: string;
  master_filters?: Record<string, string>;
}

interface LMSMasterDashboardProps {
  activeTab: LMSMasterReportKey;
  onAskMore?: (contextQuery: string, contextData?: any) => void;
  onContextChange?: (context: LMSMasterContext) => void;
}

type SortDirection = 'asc' | 'desc';

interface TableSort {
  key: string;
  direction: SortDirection;
}

export const lmsMasterReportTabs: Array<[LMSMasterReportKey, string]> = [
  ['branch-report', 'Branch Report'],
  ['store-marketing-attributes', 'Store Marketing Attributes'],
  ['store-attributes', 'Store Attributes'],
];

const columnMenuLabelOrder = [
  'Branch Code',
  'Branch Name',
  'Shop Number',
  'Store Type',
  'Store Type Name',
  'Street Address',
];

const TableLoadingRows: React.FC<{ columnCount: number; rowCount?: number }> = ({ columnCount, rowCount = 8 }) => {
  const safeColumnCount = Math.max(columnCount, 1);
  return (
    <>
      {Array.from({ length: rowCount }).map((_, rowIndex) => (
        <tr key={`loading-row-${rowIndex}`} className="animate-pulse bg-white dark:bg-slate-900">
          {Array.from({ length: safeColumnCount }).map((__, columnIndex) => (
            <td key={`loading-cell-${rowIndex}-${columnIndex}`} className="px-4 py-3">
              <div className="h-4 w-full min-w-24 rounded bg-slate-200 dark:bg-slate-700" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
};

const getReportTitle = (activeTab: LMSMasterReportKey): string =>
  lmsMasterReportTabs.find(([key]) => key === activeTab)?.[1] || 'LMS Master Report';

const valueText = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  return String(value);
};

const isTruthyBooleanText = (value: unknown): boolean =>
  typeof value === 'string' && ['true', 'yes'].includes(value.trim().toLowerCase());

const isFalsyBooleanText = (value: unknown): boolean =>
  typeof value === 'string' && ['false', 'no'].includes(value.trim().toLowerCase());

const formatNumber = (value: unknown, forceDecimals = false): string => {
  if (value === null || value === undefined || value === '') {
    return valueText(value);
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return valueText(value);
  }
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: forceDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(numeric);
};

const renderCellValue = (column: LMSMasterColumn, value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  if (typeof value === 'boolean') {
    return value ? 'True' : 'False';
  }
  if (isTruthyBooleanText(value)) {
    return 'True';
  }
  if (isFalsyBooleanText(value)) {
    return 'False';
  }
  if (column.numeric) {
    return formatNumber(value);
  }
  return String(value);
};

const compareTableValues = (first: unknown, second: unknown): number => {
  const firstNumber = Number(valueText(first).replace(/,/g, ''));
  const secondNumber = Number(valueText(second).replace(/,/g, ''));
  if (Number.isFinite(firstNumber) && Number.isFinite(secondNumber)) {
    return firstNumber - secondNumber;
  }

  const firstTime = Date.parse(valueText(first));
  const secondTime = Date.parse(valueText(second));
  if (Number.isFinite(firstTime) && Number.isFinite(secondTime)) {
    return firstTime - secondTime;
  }

  return valueText(first).localeCompare(valueText(second), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

const sortRowsByColumn = (
  sourceRows: Record<string, unknown>[],
  sort: TableSort | null,
): Record<string, unknown>[] => {
  if (!sort) {
    return sourceRows;
  }

  const directionMultiplier = sort.direction === 'asc' ? 1 : -1;
  return [...sourceRows].sort((first, second) =>
    compareTableValues(first[sort.key], second[sort.key]) * directionMultiplier
  );
};

const LMSMasterDashboard: React.FC<LMSMasterDashboardProps> = ({ activeTab, onAskMore, onContextChange }) => {
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [columns, setColumns] = useState<LMSMasterColumn[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [hiddenColumnKeys, setHiddenColumnKeys] = useState<string[]>([]);
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [tableSort, setTableSort] = useState<TableSort | null>(null);
  const columnMenuRef = useRef<HTMLDivElement | null>(null);

  const title = getReportTitle(activeTab);
  const selectableColumns = useMemo(() => columns.filter((column) => !column.hidden), [columns]);
  const columnMenuColumns = useMemo(() => {
    return [...selectableColumns].sort((first, second) => {
      const firstIndex = columnMenuLabelOrder.indexOf(first.label);
      const secondIndex = columnMenuLabelOrder.indexOf(second.label);
      if (firstIndex !== -1 || secondIndex !== -1) {
        if (firstIndex === -1) return 1;
        if (secondIndex === -1) return -1;
        return firstIndex - secondIndex;
      }
      return selectableColumns.indexOf(first) - selectableColumns.indexOf(second);
    });
  }, [selectableColumns]);
  const visibleColumns = useMemo(
    () => selectableColumns.filter((column) => !hiddenColumnKeys.includes(column.key)),
    [hiddenColumnKeys, selectableColumns]
  );
  const allColumnsVisible = selectableColumns.length > 0 && hiddenColumnKeys.length === 0;

  useEffect(() => {
    let cancelled = false;

    const loadReport = async () => {
      setIsLoading(true);
      setError(null);
      setColumnFilters({});
      try {
        const report = await fetchLMSMasterReport(activeTab);
        if (!cancelled) {
          setRows(report.rows);
          setColumns(report.columns);
        }
      } catch (loadError) {
        if (!cancelled) {
          setRows([]);
          setColumns([]);
          setError(loadError instanceof Error ? loadError.message : 'Failed to load LMS Master data');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadReport();
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  useEffect(() => {
    setColumnFilters({});
    setHiddenColumnKeys([]);
    setShowColumnMenu(false);
    setTableSort(null);
    setCurrentPage(1);
  }, [activeTab]);

  useEffect(() => {
    setCurrentPage(1);
  }, [columnFilters, hiddenColumnKeys, pageSize, tableSort]);

  useEffect(() => {
    if (!showColumnMenu) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (!columnMenuRef.current?.contains(event.target as Node)) {
        setShowColumnMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnMenu]);

  useEffect(() => {
    const selectableKeys = new Set(selectableColumns.map((column) => column.key));
    setHiddenColumnKeys((current) => current.filter((key) => selectableKeys.has(key)));
  }, [selectableColumns]);

  useEffect(() => {
    onContextChange?.({
      report_family: 'lms-master',
      active_tab: activeTab,
      report_title: title,
      report_view: 'table',
    });
  }, [activeTab, onContextChange, title]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      return visibleColumns.every((column) => {
        const filterValue = (columnFilters[column.key] || '').trim().toLowerCase();
        if (!filterValue) {
          return true;
        }
        return valueText(row[column.key]).toLowerCase().includes(filterValue);
      });
    });
  }, [columnFilters, rows, visibleColumns]);

  const sortedRows = useMemo(() => sortRowsByColumn(filteredRows, tableSort), [filteredRows, tableSort]);
  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * pageSize;
  const displayedRows = sortedRows.slice(startIndex, startIndex + pageSize);
  const endIndex = Math.min(startIndex + displayedRows.length, sortedRows.length);
  const pageNumbers = useMemo(() => {
    const pages: number[] = [];
    const firstPage = Math.max(1, Math.min(safeCurrentPage - 2, totalPages - 4));
    const lastPage = Math.min(totalPages, firstPage + 4);
    for (let page = firstPage; page <= lastPage; page += 1) {
      pages.push(page);
    }
    return pages;
  }, [safeCurrentPage, totalPages]);

  const buildTableAskMoreContext = (contextQuery: string) => {
    const data = filteredRows.slice(0, 500).map((row) =>
      visibleColumns.reduce<Record<string, unknown>>((record, column) => {
        record[column.label] = row[column.key];
        return record;
      }, {})
    );
    return {
      title: `${title} Data`,
      users_query: contextQuery,
      generated_sql_query: 'Built-in LMS Master report table',
      query_response: JSON.stringify(data),
      data,
      last_data_snapshot: data,
      source_row_count: filteredRows.length,
      visualization_type: 'table',
    };
  };

  const handleColumnFilterChange = (key: string, value: string) => {
    setColumnFilters((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleTableSort = (columnKey: string) => {
    setTableSort((currentSort) => {
      if (currentSort?.key !== columnKey) {
        return { key: columnKey, direction: 'asc' };
      }

      return {
        key: columnKey,
        direction: currentSort.direction === 'asc' ? 'desc' : 'asc',
      };
    });
    setCurrentPage(1);
  };

  const handleToggleColumn = (columnKey: string) => {
    setHiddenColumnKeys((current) => {
      if (current.includes(columnKey)) {
        return current.filter((key) => key !== columnKey);
      }
      if (visibleColumns.length <= 1) {
        return current;
      }
      return [...current, columnKey];
    });
  };

  const handleSelectAllColumns = () => {
    setHiddenColumnKeys([]);
  };

  const handleHideAllColumns = () => {
    const [, ...columnsToHide] = selectableColumns;
    setHiddenColumnKeys(columnsToHide.map((column) => column.key));
  };

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const exportRows = filteredRows.map((row) =>
        visibleColumns.reduce<Record<string, unknown>>((acc, col) => {
          acc[col.label] = row[col.key];
          return acc;
        }, {}),
      );
      await exportQueryResponse(exportRows, `${title} Data`, 'table');
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white/95 shadow-sm dark:border-slate-700 dark:bg-slate-900/90">
      <div className="flex flex-col gap-4 border-b border-slate-200 px-6 py-5 dark:border-slate-700 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">
            LMS Master Report
          </div>
          <h2 className="mt-1 text-xl font-bold text-slate-950 dark:text-slate-50">{title}</h2>
        </div>
      </div>

      <div className="px-6 py-5">
        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
            {error}
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
              <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-4 dark:border-slate-700 md:flex-row md:items-center md:justify-between">
                <div>
                  <h3 className="font-bold text-slate-800 dark:text-slate-100">{title} Data</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Showing {filteredRows.length ? startIndex + 1 : 0} to {endIndex} of {filteredRows.length} rows
                    {filteredRows.length !== rows.length ? ` (filtered from ${rows.length} rows)` : ''}
                  </p>
                </div>
		                <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row md:items-center">
                  <label className="flex h-10 items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                    Show:
                    <select
                      value={pageSize}
                      onChange={(event) => setPageSize(Number(event.target.value))}
                      className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm font-medium dark:border-slate-600 dark:bg-slate-800"
                    >
                      {[10, 25, 50, 100].map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </label>
	                  <div ref={columnMenuRef} className="relative">
                    <button
                      type="button"
                      onClick={() => setShowColumnMenu((current) => !current)}
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                      title="Show or hide columns"
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>
                        arrow_drop_down
                      </span>
                      Columns
                    </button>
                    {showColumnMenu && (
                      <div className="absolute right-0 z-30 mt-2 w-80 border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900">
                        <div className="px-3 pt-2 text-sm font-semibold text-neutral-800 dark:text-slate-100">
                          Columns
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowColumnMenu((current) => !current)}
                          className="mx-3 mt-1 flex h-9 w-[calc(100%-1.5rem)] items-center justify-between border border-slate-200 bg-white px-2 text-left text-sm text-neutral-500 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                        >
                          <span>All</span>
                          <span className="material-symbols-outlined text-neutral-500" style={{ fontSize: '18px' }}>
                            expand_less
                          </span>
                        </button>
                        <div className="max-h-72 overflow-y-auto px-3 py-1">
                          <label className="flex h-8 cursor-pointer items-center gap-3 text-sm text-neutral-800 hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-slate-800">
                            <input
                              type="checkbox"
                              checked={allColumnsVisible}
                              onChange={() => {
                                if (allColumnsVisible) {
                                  handleHideAllColumns();
                                } else {
                                  handleSelectAllColumns();
                                }
                              }}
                              className="h-4 w-4 rounded-none border-neutral-800 bg-neutral-900 text-neutral-900 accent-neutral-900 focus:ring-0"
                            />
                            <span>Select all</span>
                          </label>
                          {columnMenuColumns.map((column) => {
                            const checked = !hiddenColumnKeys.includes(column.key);
                            return (
                              <label
                                key={`column-toggle-${column.key}`}
                                className="flex h-8 cursor-pointer items-center gap-3 text-sm text-neutral-800 hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-slate-800"
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => handleToggleColumn(column.key)}
                                  disabled={checked && visibleColumns.length <= 1}
                                  className="h-4 w-4 rounded-none border-neutral-800 bg-neutral-900 text-neutral-900 accent-neutral-900 focus:ring-0 disabled:opacity-50"
                                />
                                <span className="truncate">{column.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
	                  {onAskMore && (
                    <button
                      type="button"
                      onClick={() => {
                        const contextQuery = `Ask about ${title} data`;
                        onAskMore(contextQuery, buildTableAskMoreContext(contextQuery));
                      }}
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-emerald-500 px-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-600"
                      title="Ask more about this data"
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                        auto_awesome
                      </span>
                      Ask More
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleExport}
                    disabled={isExporting || isLoading || filteredRows.length === 0}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-blue-600 text-white shadow-sm transition-colors hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Export"
                  >
                    {isExporting ? (
                      <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    ) : (
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 dark:bg-slate-800/80 dark:text-slate-300">
	                    <tr>
	                      {visibleColumns.map((column) => (
	                        <th key={column.key} className="whitespace-nowrap px-4 py-3 text-left font-bold">
	                          <button
                              type="button"
                              onClick={() => handleTableSort(column.key)}
                              className="group inline-flex items-center gap-1.5 text-left font-bold text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
                              title={`Sort by ${column.label}`}
                            >
                              <span>{column.label}</span>
                              <span
                                className={`material-symbols-outlined text-[15px] leading-none transition-opacity ${
                                  tableSort?.key === column.key ? 'opacity-100' : 'opacity-35 group-hover:opacity-80'
                                }`}
                                aria-hidden="true"
                                style={{ fontSize: '15px' }}
                              >
                                {tableSort?.key === column.key
                                  ? tableSort.direction === 'asc'
                                    ? 'arrow_upward'
                                    : 'arrow_downward'
                                  : 'unfold_more'}
                              </span>
                            </button>
	                        </th>
	                      ))}
	                    </tr>
                    <tr>
                      {visibleColumns.map((column) => (
                        <th key={`${column.key}-filter`} className="px-4 pb-3">
                          <input
                            type="search"
                            value={columnFilters[column.key] || ''}
                            onChange={(event) => handleColumnFilterChange(column.key, event.target.value)}
                            className="w-full min-w-40 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-normal text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                            placeholder={`Filter ${column.label}...`}
                          />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {isLoading ? (
                      <TableLoadingRows columnCount={visibleColumns.length} rowCount={Math.min(pageSize, 8)} />
                    ) : displayedRows.length === 0 ? (
                      <tr>
                        <td className="px-4 py-8 text-center text-slate-500" colSpan={Math.max(visibleColumns.length, 1)}>
                          No data available
                        </td>
                      </tr>
                    ) : (
                      displayedRows.map((row, rowIndex) => (
                        <tr
                          key={`${activeTab}-${startIndex + rowIndex}`}
                          className="bg-white text-slate-700 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800/70"
                        >
                          {visibleColumns.map((column) => {
                            const cellValue = renderCellValue(column, row[column.key]);
                            const isUrlColumn = column.key.toLowerCase().includes('url') || column.label.toLowerCase() === 'url';
                            return (
                              <td key={column.key} className="whitespace-nowrap px-4 py-3">
                                <div
                                  className={isUrlColumn ? 'max-w-72 truncate' : undefined}
                                  title={isUrlColumn ? cellValue : undefined}
                                >
                                  {cellValue}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {filteredRows.length > pageSize && (
                <div className="flex flex-col gap-3 border-t border-slate-200 px-4 py-3 dark:border-slate-700 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    Page {safeCurrentPage} of {totalPages}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                      disabled={safeCurrentPage === 1}
                      className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      Previous
                    </button>
                    {pageNumbers.map((page) => (
                      <button
                        key={page}
                        type="button"
                        onClick={() => setCurrentPage(page)}
                        className={`min-w-9 rounded-md border px-3 py-1.5 text-sm font-semibold transition-colors ${
                          safeCurrentPage === page
                            ? 'border-blue-600 bg-blue-600 text-white'
                            : 'border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                      disabled={safeCurrentPage === totalPages}
                      className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default LMSMasterDashboard;
